import {
  DELETION_FLAG,
  FallRiskEntry,
  RESIDENT_STATE_ORDER1,
  ResidentState,
  Status,
  UserKey,
} from "@packages/core";
import { Link, createFileRoute } from "@tanstack/react-router";
import { Fragment, useCallback, useMemo, useState } from "react";
import { LuListFilter } from "react-icons/lu";
import { Temporal } from "temporal-polyfill";

import {
  BedCard,
  ButtonStyle,
  FallRiskEntrySubscriber,
  GroupSelector,
  PageName,
  StatusSubscriber,
} from "#components";
import {
  useBedInfiniteQuery,
  useGroupInfiniteQuery,
  useSelectedGroupKeys,
  useTranslate,
} from "#services";
import { css, cx } from "#styled-system/css";

export const Route = createFileRoute("/_authenticated/facilities/$facilityKey/")({
  component: Component,
});

function Component() {
  const { facilityKey } = Route.useParams();

  const translate = useTranslate();
  const bedInfiniteQuery = useBedInfiniteQuery({
    index: "facilityKey-deleted-gsi",
    condition: [
      ["facilityKey", "=", facilityKey],
      ["deleted", "=", DELETION_FLAG.DEFAULT],
    ],
  });
  const groupInfiniteQuery = useGroupInfiniteQuery({
    index: "facilityKey-deleted",
    condition: [
      ["facilityKey", "=", facilityKey],
      ["deleted", "=", DELETION_FLAG.DEFAULT],
    ],
  });
  const [fallRiskEntries, setFallRiskEntries] = useState(new Map<UserKey, FallRiskEntry>());
  const [statuses, setStatuses] = useState(new Map<UserKey, Status>());
  const [groupSelectorOpen, setGroupSelectorOpen] = useState(false);
  const [selectedGroupKeys, setSelectedGroupKeys] = useSelectedGroupKeys(facilityKey);

  const beds = useMemo(
    function () {
      function compareDateTimes(
        a: Temporal.PlainDateTime | null | undefined,
        b: Temporal.PlainDateTime | null | undefined,
      ) {
        return !a && !b ? 0 : !a ? 1 : !b ? -1 : Temporal.PlainDateTime.compare(a, b);
      }

      function compareNumbers(a: number | null | undefined, b: number | null | undefined) {
        return a == null && b == null ? 0 : a == null ? 1 : b == null ? -1 : a - b;
      }

      function compareResidentStates(
        a: ResidentState | null | undefined,
        b: ResidentState | null | undefined,
      ) {
        return !a && !b
          ? 0
          : !a
            ? 1
            : !b
              ? -1
              : RESIDENT_STATE_ORDER1.indexOf(a) - RESIDENT_STATE_ORDER1.indexOf(b);
      }

      return bedInfiniteQuery.data?.pages
        .flatMap(function (page) {
          return page.beds.filter(function (bed) {
            // TODO: グループが浸透したら`groupKey`の非ヌルアサーションを外す。
            return selectedGroupKeys.length === 0 || selectedGroupKeys.includes(bed.groupKey!);
          });
        })
        .sort(function (a, b) {
          // 入居者が居ないベッドを後にする。
          if (!a.userKey && !b.userKey) {
            return a.bedKey.localeCompare(b.bedKey);
          } else if (!a.userKey) {
            return 1;
          } else if (!b.userKey) {
            return -1;
          }

          const fallRiskEntryA = fallRiskEntries.get(a.userKey);
          const statusA = statuses.get(a.userKey);
          const fallRiskEntryB = fallRiskEntries.get(b.userKey);
          const statusB = statuses.get(b.userKey);

          if (statusA?.alerted && !statusA.confirmed && !(statusB?.alerted && !statusB.confirmed)) {
            return -1;
          }

          if (statusB?.alerted && !statusB.confirmed && !(statusA?.alerted && !statusA.confirmed)) {
            return 1;
          }

          if (fallRiskEntryA && !fallRiskEntryB) {
            return -1;
          }

          if (fallRiskEntryB && !fallRiskEntryA) {
            return 1;
          }

          if (statusA && !statusB) {
            return -1;
          }

          if (statusB && !statusA) {
            return 1;
          }

          return (
            compareNumbers(fallRiskEntryB?.fallRisk, fallRiskEntryA?.fallRisk) ||
            compareResidentStates(statusA?.state, statusB?.state) ||
            compareDateTimes(statusB?.alertedAt, statusA?.alertedAt) ||
            compareDateTimes(statusB?.transitionedAt, statusA?.transitionedAt) ||
            a.bedKey.localeCompare(b.bedKey)
          );
        });
    },
    [fallRiskEntries, bedInfiniteQuery.data, selectedGroupKeys, statuses],
  );

  const groups = useMemo(
    function () {
      return groupInfiniteQuery.data?.pages
        .flatMap(function (page) {
          return page.groups;
        })
        .sort(function (a, b) {
          return a.name.localeCompare(b.name) || a.groupKey.localeCompare(b.groupKey);
        });
    },
    [groupInfiniteQuery.data],
  );

  const handleFallRiskEntryChange = useCallback(function (
    fallRiskEntry: FallRiskEntry | null | undefined,
  ) {
    setFallRiskEntries(function (oldFallRiskEntries) {
      const newFallRiskEntries = new Map(oldFallRiskEntries);
      if (fallRiskEntry?.userKey) {
        newFallRiskEntries.set(fallRiskEntry.userKey, fallRiskEntry);
      }
      return newFallRiskEntries;
    });
  }, []);

  const handleStatusChange = useCallback(function (status: Status | null | undefined) {
    setStatuses(function (statuses) {
      const newStatuses = new Map(statuses);
      if (status?.userKey) {
        newStatuses.set(status.userKey, status);
      }
      return newStatuses;
    });
  }, []);

  return (
    <main>
      <PageName>{translate("%ResidentList.0%")}</PageName>

      {beds?.map(function (bed) {
        return (
          <Fragment key={bed.bedKey}>
            <FallRiskEntrySubscriber onChange={handleFallRiskEntryChange} userKey={bed.userKey} />
            <StatusSubscriber onChange={handleStatusChange} userKey={bed.userKey} />
          </Fragment>
        );
      })}

      <div className={css({ display: "flex" })}>
        <button
          className={cx(
            ButtonStyle({
              icon: true,
              appearance: "outlined",
              padding: "12px",
              borderRadius: "6px",
              color: "outlined",
            }),
            // デフォルトグループ以外のグループが存在しない場合は表示しない。
            (!groups || groups.length <= 1) && css({ display: "none" }),
          )}
          onClick={function () {
            setGroupSelectorOpen(function (open) {
              return !open;
            });
          }}
        >
          <LuListFilter className={css({ height: "16px", width: "16px" })} />
        </button>
      </div>

      <GroupSelector
        defaultValue={selectedGroupKeys}
        onCancel={function () {
          setGroupSelectorOpen(false);
        }}
        onChange={function (selectedGroupKeys) {
          setSelectedGroupKeys(selectedGroupKeys);
          setGroupSelectorOpen(false);
        }}
        open={groupSelectorOpen}
        options={groups?.map(function (group) {
          return group.groupKey;
        })}
      />

      <div
        className={css({
          display: "grid",
          gap: "12px",
          gridTemplateColumns: { smDown: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" },
          marginTop: "16px",
        })}
      >
        {beds?.map(function (bed) {
          return bed.userKey ? (
            <Link
              key={bed.bedKey}
              params={{ facilityKey, userKey: bed.userKey }}
              to="/facilities/$facilityKey/residents/$userKey"
            >
              <BedCard bedKey={bed.bedKey} />
            </Link>
          ) : (
            <BedCard bedKey={bed.bedKey} key={bed.bedKey} />
          );
        })}
      </div>
    </main>
  );
}
